/**
 * 主题星座的连线（design §11.10）：同一主题的各站（日子）按时间先后连成直线段，切过螺线，和顺着螺线走的星轨一眼分得开。
 * 淡金色虚线约 1.4 像素宽，外面一圈很淡的光晕；虚线朝未来的方向缓缓流动。进入时从第一站到最后一站一段段画出来。
 * WebGL 的 THREE.Line 只有 1 像素宽，这里用 three.js 自带的 Line2（三角形条带画的粗线，同一个包，不算新依赖）。
 */
import * as THREE from 'three';
import { Line2 } from 'three/addons/lines/Line2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';

const GOLD = 0xf3cf8b;
/** 虚线一段多长、隔多长（世界单位），往未来流多快（单位 / 秒） */
const DASH = 0.5, GAP = 0.32, FLOW = 0.25;

function material(width: number, dashed: boolean): LineMaterial {
  const m = new LineMaterial({ color: GOLD, linewidth: width, dashed, dashSize: DASH, gapSize: GAP, transparent: true, opacity: 0 });
  m.depthTest = false;
  m.depthWrite = false;
  m.blending = THREE.AdditiveBlending;
  m.toneMapped = false;
  return m;
}

export class ConstellationLines {
  readonly group = new THREE.Group();
  private readonly mainMat = material(1.4, true);
  private readonly glowMat = material(5, false);
  private main: Line2 | null = null;
  private glow: Line2 | null = null;
  private segments = 0;

  constructor() {
    this.group.renderOrder = 3;
  }

  /** 各站的位置（按时间先后）；少于两站时没有线 */
  set(points: readonly THREE.Vector3[]): void {
    this.clear();
    this.segments = Math.max(0, points.length - 1);
    if (!this.segments) return;
    const flat = points.flatMap((p) => [p.x, p.y, p.z]);
    const make = (mat: LineMaterial) => {
      const geo = new LineGeometry();
      geo.setPositions(flat);
      const line = new Line2(geo, mat);
      line.computeLineDistances();
      line.frustumCulled = false;
      line.renderOrder = 3;
      this.group.add(line);
      return line;
    };
    this.glow = make(this.glowMat);
    this.main = make(this.mainMat);
  }

  /** 线宽按 CSS 像素算：resolution 用画布的 CSS 尺寸 */
  setResolution(width: number, height: number): void {
    this.mainMat.resolution.set(width, height);
    this.glowMat.resolution.set(width, height);
  }

  /**
   * opacity 0–1；reveal 0–1 是画出来了多少段（进入时从第一站往最后一站画）；flow 是虚线往未来流动了多远（世界单位）
   */
  update(opacity: number, reveal: number, flow: number): void {
    this.group.visible = opacity > 0.002 && this.segments > 0;
    if (!this.group.visible) return;
    this.mainMat.opacity = 0.8 * opacity;
    this.glowMat.opacity = 0.08 * opacity;
    // 虚线的花纹往线的起点那边挪，看起来就是往未来流
    this.mainMat.dashOffset = -flow;
    const shown = Math.max(1, Math.ceil(reveal * this.segments));
    for (const line of [this.main, this.glow]) if (line) line.geometry.instanceCount = shown;
  }

  /** 流动的速度（单位 / 秒） */
  static readonly flowSpeed = FLOW;

  private clear(): void {
    for (const line of [this.main, this.glow]) {
      if (!line) continue;
      this.group.remove(line);
      line.geometry.dispose();
    }
    this.main = this.glow = null;
    this.segments = 0;
  }
}
